import { InjectionToken } from '@angular/core';
import { DedupOptions, ErrorReportContext, RedactionRule } from '@imagise/entities';

/**
 * Host-supplied configuration for the error-reporting feature. Everything
 * app-specific is passed here so the feature stays portable across apps
 * (`imagise-app`, `imagise-cloud`, ...) with no hard-coded values.
 */
export interface ErrorReportingConfig {
  /** Failed application name sent with reports, e.g. `imagise-app`. */
  appName: string;

  /** Application version for triage; falls back to the `APP_VERSION` token. */
  appVersion?: string | null;

  /** `true` for production builds. */
  isProduction: boolean;

  /**
   * Base URL of the backend that serves the error and feedback endpoints. Optional: when set,
   * the feature provides `BASE_URL_PROVIDER` itself (for apps that don't already
   * provide one, e.g. `imagise-cloud`). When omitted, the app's existing
   * `BASE_URL_PROVIDER` is used.
   */
  baseUrl?: string;

  /** Route to navigate to on a fatal crash. Defaults to `/error`. */
  errorRoutePath?: string;

  /** Overrides for crash-loop dedup / rate limiting. */
  dedupOptions?: Partial<DedupOptions>;

  /** Extra redaction rules, appended to the built-in defaults. */
  redactionRules?: RedactionRule[];

  /**
   * Whether to attach `window.onerror` / `unhandledrejection` listeners.
   * Defaults to `true`.
   */
  enableBrowserListeners?: boolean;
}

/** DI token carrying the active {@link ErrorReportingConfig}. */
export const ERROR_REPORTING_CONFIG = new InjectionToken<ErrorReportingConfig>(
  'ERROR_REPORTING_CONFIG'
);

/**
 * Presents a non-fatal error to the user (e.g. an unobtrusive overlay prompt).
 * Implemented by the UI layer and registered via {@link ERROR_REPORT_PROMPTER};
 * the reporting service stays decoupled from any concrete UI.
 */
export interface ErrorReportPrompter {
  prompt(context: ErrorReportContext): void;
  close?(): void;
}

/** Optional DI token for a non-fatal {@link ErrorReportPrompter}. */
export const ERROR_REPORT_PROMPTER = new InjectionToken<ErrorReportPrompter>(
  'ERROR_REPORT_PROMPTER'
);

/** The app owns authentication; this feature never stores tokens or imports app services. */
export interface ErrorReportFeedbackAuth {
  isAuthenticated(): boolean;
  getAccessToken(): Promise<string | null>;
}

export const ERROR_REPORT_FEEDBACK_AUTH = new InjectionToken<ErrorReportFeedbackAuth | null>(
  'ERROR_REPORT_FEEDBACK_AUTH', { providedIn: 'root', factory: () => null }
);
